// Static insights data - no backend required
// Add, edit, or remove insights directly in this file

export const insights = [
  {
    id: "ai-weekly-reviews",
    title: "How I Use AI to Structure My Weekly Reviews",
    summary: "A practical framework for using AI assistants to run effective personal and professional weekly reviews that actually drive improvement.",
    content: `Weekly reviews are powerful, but most people either skip them or make them too complicated. Here's how I use AI to make mine both effective and sustainable.

## The Problem with Traditional Weekly Reviews

Most weekly reviews fail because they're either too vague ("How was my week?") or too detailed (50-item checklists). AI helps us find the middle ground.

## My AI-Assisted Framework

### Step 1: Brain Dump
I start by telling my AI assistant everything that happened this week. No structure, just talking. The AI helps organize this chaos into categories.

### Step 2: Pattern Recognition
I ask the AI to identify patterns. What kept coming up? Where did I spend most of my energy? What got consistently pushed off?

### Step 3: Decision Quality Check
For major decisions I made, I ask the AI to help me evaluate my reasoning. Was I reactive or strategic? Did I consider alternatives?

### Step 4: Next Week Setup
Based on the review, the AI helps me identify 3 priorities for next week. Not a to-do list - just three things that matter.

## Results

Since implementing this system, I've noticed:
- Better pattern recognition in my work habits
- Faster identification of time sinks
- More intentional decision-making
- Less anxiety about "missing something"

The key is consistency. AI makes it easier to show up every week because the cognitive load is shared.`,
    category: "ai",
    tags: ["productivity", "workflows", "decision-making"],
    youtube_url: null,
    created_at: "2025-01-15"
  },
  {
    id: "ai-research-assistant",
    title: "Building a Personal Research Assistant with AI",
    summary: "How to set up AI tools to help you research topics deeply without getting lost in information overload.",
    content: `Information overload is real. Every time I start researching a topic, I end up with 47 browser tabs and no clear conclusions. Here's how I use AI to fix that.

## The Research Problem

Traditional research is exhausting because:
- There's always "one more source" to check
- It's hard to know when you have "enough" information
- Synthesizing information across sources takes forever
- You forget what you learned two days ago

## My AI Research System

### Setting Up the Assistant

I treat my AI assistant as a research partner, not a search engine. The key difference: I give it context about what I already know and what I'm trying to decide.

### The Research Loop

1. **Define the Question**: Not "tell me about X" but "I'm trying to decide Y, and I need to understand X to do that"
2. **Initial Synthesis**: Ask AI to give me the key frameworks and major perspectives
3. **Gap Identification**: Ask what I should know but probably don't
4. **Deep Dives**: Focused research on specific gaps
5. **Decision Mapping**: How does this new information change my options?

### Managing Notes

I keep my research notes in a simple system and periodically ask AI to summarize what I've learned and what questions remain.

## Why This Works

The AI doesn't replace research - it makes research sustainable. Instead of drowning in information, I have a structured conversation that builds understanding over time.`,
    category: "ai",
    tags: ["research", "productivity", "decision-making"],
    youtube_url: null,
    created_at: "2025-01-10"
  },
  {
    id: "ai-small-business",
    title: "AI Workflows for Small Business Owners",
    summary: "Practical, low-complexity AI workflows that small business owners can implement this week to save time on recurring tasks.",
    content: `Small business owners are busy. You don't have time for complicated AI setups. Here are five workflows you can implement this week.

## Workflow 1: Customer Email Templates

**Time saved**: 30+ minutes daily

Instead of writing every customer email from scratch, create a "template brain" in your AI tool. Feed it examples of your best emails, your tone guidelines, and common scenarios.

When you need to reply, give the AI the context and let it draft. Your job is just to review and personalize.

## Workflow 2: Meeting Prep Summaries

**Time saved**: 15-20 minutes per meeting

Before any important meeting, ask AI to help you prepare:
- Review previous notes/emails with this person
- List key topics you should cover
- Identify potential questions they might ask
- Summarize any recent changes in their industry/situation

## Workflow 3: Content Repurposing

**Time saved**: 2-3 hours weekly

Wrote a detailed email to a client? That's content. Have AI help you turn it into:
- A LinkedIn post
- A section of your FAQ
- An internal process document
- A template for similar situations

## Workflow 4: Decision Documentation

**Time saved**: Prevents future mistakes

When you make important decisions, have AI help you document:
- What you decided
- Why you decided it
- What alternatives you considered
- What would make you revisit this decision

## Workflow 5: Weekly Business Review

**Time saved**: Makes existing review time more valuable

Use AI to help you analyze your week's metrics, customer feedback, and operational notes. Pattern recognition at scale.

## Start Simple

Pick one workflow. Use it for a week. Then add another. The goal isn't to AI-ify everything - it's to reclaim time for work that actually needs your human judgment.`,
    category: "ai",
    tags: ["small business", "workflows", "productivity"],
    youtube_url: null,
    created_at: "2025-01-05"
  },
  {
    id: "position-sizing",
    title: "Position Sizing: The Risk Management Fundamental Most Ignore",
    summary: "Why position sizing matters more than entry points, and a practical framework for determining how much to allocate to any trade.",
    content: `Most retail investors obsess over when to buy. The more important question: how much to buy. This is position sizing, and getting it wrong can destroy your portfolio even when your analysis is right.

## Disclaimer

I'm not a financial advisor. This is educational content about how I think about position sizing. Always do your own research.

## Why Position Sizing Matters

Imagine you find what looks like a great opportunity. You go all-in. It drops 40%. Even if you were right about the eventual direction, can you hold through that drawdown? Most can't.

Now imagine you sized it at 5% of your portfolio. A 40% drop means a 2% portfolio impact. Much easier to stay rational.

## My Position Sizing Framework

### Step 1: Define Your Risk Per Trade

I never risk more than 1-2% of my total portfolio on any single trade. This means if I'm completely wrong and hit my stop loss, I lose 1-2%.

### Step 2: Determine Your Stop Loss

Before entering any position, I know where I'm wrong. What price level would invalidate my thesis? That's my stop loss.

### Step 3: Calculate Position Size

Position Size = (Portfolio Risk Amount) / (Entry Price - Stop Loss Price)

Example:
- Portfolio: $10,000
- Max risk: 1% = $100
- Stock price: $50
- Stop loss: $45 (10% below entry)
- Position size: $100 / $5 = 20 shares = $1,000 (10% of portfolio)

### Step 4: Adjust for Conviction

Higher conviction doesn't mean bigger bets. It means tighter stops or more favorable entry points. The math stays the same.

## Common Mistakes

1. **Averaging down without a plan**: This is just increasing position size without new information
2. **Ignoring correlation**: Five "small" positions in correlated assets = one big position
3. **Revenge sizing**: Losing makes you want to bet bigger to "make it back"

## The Bottom Line

Position sizing is boring. It doesn't make for exciting trading stories. But it's the difference between staying in the game and blowing up.`,
    category: "markets",
    tags: ["risk management", "trading", "fundamentals"],
    youtube_url: null,
    created_at: "2025-01-12"
  },
  {
    id: "support-resistance",
    title: "Technical Analysis Basics: Support, Resistance, and Why They Work",
    summary: "A practical introduction to support and resistance levels - what they are, why they form, and how I use them in my analysis.",
    content: `Technical analysis gets a bad reputation. Some of it is deserved - a lot of TA content is overcomplicated nonsense. But the fundamentals, like support and resistance, are actually just applied psychology.

## Disclaimer

This is educational content, not financial advice. I'm a retail investor sharing my learning, not a licensed professional.

## What Are Support and Resistance?

**Support**: A price level where buying pressure tends to overcome selling pressure. The price has trouble falling below this level.

**Resistance**: A price level where selling pressure tends to overcome buying pressure. The price has trouble rising above this level.

## Why Do They Form?

These levels work because of human psychology:

1. **Memory**: People remember what they paid. If you bought at $50 and it dropped to $40, you might sell when it gets back to $50 to "break even."

2. **Round Numbers**: $100, $50, $10 - these attract attention and orders cluster around them.

3. **Previous Pivots**: If a price bounced at $45 before, traders remember and place orders there again.

4. **Moving Averages**: Many traders watch the 50-day, 200-day moving averages. When price approaches these, orders cluster.

## How I Use Support and Resistance

### For Entry Points

I prefer entering near support levels. Why? Better risk/reward. My stop can be just below support, meaning I know quickly if I'm wrong.

### For Exit Points

Resistance levels are where I consider taking profits. Not necessarily selling everything, but reducing size.

### For Stop Losses

If a support level breaks decisively, it often becomes resistance. This is where I know my thesis was wrong.

## Common Mistakes

1. **Treating levels as exact prices**: These are zones, not precise numbers
2. **Ignoring context**: A support level on a daily chart matters more than one on a 5-minute chart
3. **Fighting the trend**: Support in a downtrend is weaker than support in an uptrend

## Practice

The best way to learn is to draw levels on charts and track what happens. No money needed - just observation.`,
    category: "markets",
    tags: ["technical analysis", "trading", "education"],
    youtube_url: null,
    created_at: "2025-01-08"
  },
  {
    id: "passive-income-ideas",
    title: "Passive Income Ideas for Working Professionals",
    summary: "Realistic passive income strategies that complement a full-time job - what works, what doesn't, and how to get started.",
    content: `The internet is full of passive income advice written by people who seem to have infinite time and capital. Here's a more realistic take for people with full-time jobs.

## Disclaimer

Not financial advice. Just my perspective on what I've learned exploring passive income while working full-time.

## The Passive Income Spectrum

Nothing is truly passive. Everything requires either:
- Upfront time investment
- Ongoing maintenance
- Capital

The question is: what's the ratio of input to output, and does it fit your life?

## What I've Explored

### Dividend Investing

**Reality**: Requires significant capital to generate meaningful income. $100,000 at 4% yield = $4,000/year = $333/month.

**Pros**: Truly passive once set up, compounds over time
**Cons**: Slow, requires capital, dividend stocks can underperform growth

**Verdict**: Good as part of a long-term strategy, not a quick win

### Content Creation (YouTube, Blog)

**Reality**: 6-18 months before meaningful revenue. Requires consistent time investment.

**Pros**: Low startup cost, unlimited upside, builds skills
**Cons**: Extremely competitive, requires unique angle, not passive initially

**Verdict**: Good if you enjoy creating, have expertise to share, and can commit long-term

### Digital Products

**Reality**: Courses, templates, ebooks - one-time creation, ongoing sales.

**Pros**: High margins, scalable
**Cons**: Marketing is ongoing work, requires audience or SEO skills

**Verdict**: Good if you have existing expertise and audience

## What I'm Focused On

1. **Dividend growth investing**: Slow but reliable. I'm not counting on this for income now, but it's a long-term play.

2. **Content creation**: Building audience through these insights and YouTube. No shortcuts here.

3. **Skill development**: The best "passive income" for a working professional is increasing your active income through valuable skills. Higher salary = more capital to deploy.

## The Unsexy Truth

Real passive income for most people:
1. Max out retirement accounts
2. Invest in index funds
3. Increase earning potential through skills
4. Be patient

Everything else is either:
- A lot more work than advertised
- Requires more capital than most people have
- Higher risk than presented

That doesn't mean don't try other approaches. Just go in with realistic expectations.`,
    category: "markets",
    tags: ["passive income", "investing", "fundamentals"],
    youtube_url: null,
    created_at: "2025-01-03"
  }
];

// Helper function to get all insights
export const getAllInsights = () => insights;

// Helper function to get insight by ID
export const getInsightById = (id) => {
  return insights.find(insight => insight.id === id);
};

// Helper function to get insights by category
export const getInsightsByCategory = (category) => {
  if (!category || category === 'all') return insights;
  return insights.filter(insight => insight.category === category);
};
